/**
 * PlateBreakdown — small inline plate diagram next to the weight stepper.
 *
 * Renders the per-side stack as a top-down list: [bar] then descending
 * plate kg with counts. Refetches from /plate-breakdown whenever the
 * stepper changes the weight — server is canonical for plate math.
 */

import { useEffect, useState } from 'react';

interface Plate {
  kg: number;
  sidePairs: number;
}

interface BreakdownResponse {
  barKg: number;
  achievableKg: number;
  plates: Plate[];
}

interface Props {
  sessionId: number;
  exerciseId: string;
  weightKg: number;
}

export function PlateBreakdown({ exerciseId, weightKg }: Props) {
  const [data, setData] = useState<BreakdownResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ weightKg: String(weightKg) });
    // exerciseId reserved for future bar-kg lookup (trap bar, sled, etc.).
    // For now barKg defaults to 20 server-side.
    void exerciseId;
    fetch(`/api/tools/gym/plate-breakdown?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setData(json as BreakdownResponse);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [weightKg, exerciseId]);

  if (!data) return null;
  if (data.plates.length === 0) {
    return (
      <div className="plate-breakdown">
        <span className="plate-bar">{data.barKg} bar</span>
        <span className="plate-summary">= {data.achievableKg} kg</span>
      </div>
    );
  }

  const platesStr = data.plates.map((p) => `${p.sidePairs}×${p.kg}`).join(' + ');
  const off = Math.abs(data.achievableKg - weightKg) > 0.001;

  return (
    <div className="plate-breakdown">
      <span className="plate-summary">
        {data.achievableKg} kg = {data.barKg} bar + {platesStr} per side
      </span>
      {off && <span className="plate-warn">target {weightKg} kg</span>}
    </div>
  );
}
